// ── aiMapper.js — Claude Haiku 식재료명 매칭 클라이언트 ──────────
// localStorage 캐시 (30일 TTL) + synonym 우선 fallback to AI
import { normalizeFoodName } from './synonyms';

const CACHE_KEY = 'semfit_ai_map_cache_v1';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;  // 30일

// ── 캐시 헬퍼 ─────────────────────────────────────────────
function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch { return {}; }
}
function saveCache(cache) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); }
  catch (e) { console.warn('AI cache save failed', e); }
}
function cacheKey(task, name, candidatesHash = '') {
  return `${task}::${name}::${candidatesHash}`;
}
function hashCandidates(arr) {
  // 짧은 결정적 해시 (캔디데이트 변경 감지용)
  if (!Array.isArray(arr) || arr.length === 0) return '';
  let h = 0;
  const s = arr.slice(0, 30).join('|');
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return String(h);
}
function getCached(key) {
  const cache = loadCache();
  const entry = cache[key];
  if (!entry) return null;
  if (Date.now() - entry.t > TTL_MS) return null;
  return entry.v;
}
function setCached(key, value) {
  const cache = loadCache();
  cache[key] = { v: value, t: Date.now() };
  // 캐시 사이즈 제한 (오래된 것 제거)
  const keys = Object.keys(cache);
  if (keys.length > 500) {
    keys.sort((a, b) => (cache[a].t || 0) - (cache[b].t || 0));
    for (let i = 0; i < keys.length - 400; i++) delete cache[keys[i]];
  }
  saveCache(cache);
}
export function clearAiMapCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}

// ── 내부 fetch wrapper ────────────────────────────────────
async function callApi(task, name, candidates) {
  const res = await fetch('/api/ai-map', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ task, name, candidates }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`AI API ${res.status}: ${err}`);
  }
  return await res.json();
}

// ── 1) 표준 식재료명 ─────────────────────────────────────
// synonym 사전 우선, 없으면 AI 호출
export async function canonicalize(name) {
  if (!name) return '';
  const trimmed = String(name).trim();
  if (!trimmed) return '';

  // synonym으로 정규화 시도
  const fromSyn = normalizeFoodName(trimmed);
  // synonym이 변환했으면 그것을 사용 (AI 호출 절약)
  if (fromSyn !== trimmed.replace(/\s+/g, '')) {
    return fromSyn;
  }

  // 캐시
  const key = cacheKey('canonicalize', trimmed);
  const cached = getCached(key);
  if (cached !== null) return cached;

  try {
    const data = await callApi('canonicalize', trimmed, []);
    const canonical = data?.canonical || trimmed;
    setCached(key, canonical);
    return canonical;
  } catch (e) {
    console.warn('AI canonicalize failed:', e);
    return trimmed;  // 실패시 원본 반환
  }
}

// ── 2) KAMIS 후보 중 최적 매칭 ───────────────────────────
export async function pickBestKamis(name, candidates) {
  if (!name || !Array.isArray(candidates) || candidates.length === 0) return -1;

  const key = cacheKey('pickBestKamis', name, hashCandidates(candidates));
  const cached = getCached(key);
  if (cached !== null) return cached;

  try {
    const data = await callApi('pickBestKamis', name, candidates);
    const idx = Number.isInteger(data?.index) ? data.index : -1;
    setCached(key, idx);
    return idx;
  } catch (e) {
    console.warn('AI pickBestKamis failed:', e);
    return -1;
  }
}

// ── 3) 네이버 검색 결과 필터 (인덱스 배열) ────────────────
export async function filterNaver(name, candidates) {
  if (!name || !Array.isArray(candidates) || candidates.length === 0) return [];

  const key = cacheKey('filterNaver', name, hashCandidates(candidates));
  const cached = getCached(key);
  if (cached !== null) return cached;

  try {
    const data = await callApi('filterNaver', name, candidates);
    const matches = Array.isArray(data?.matches) ? data.matches.filter(i => Number.isInteger(i)) : [];
    setCached(key, matches);
    return matches;
  } catch (e) {
    console.warn('AI filterNaver failed:', e);
    return [];
  }
}

// ── 일괄 표준화 (PriceManager 일괄 매칭용) ───────────────
// 여러 식재료를 한 번에 처리, AI는 synonym fallback 항목만 호출
export async function canonicalizeAll(names) {
  const results = {};
  for (const name of names) {
    if (!name) { results[name] = ''; continue; }
    try {
      results[name] = await canonicalize(name);
    } catch (e) {
      results[name] = name;
    }
  }
  return results;
}
